// AI-CONTEXT: See docs/features/household-overview-table-and-statuses.md
// The first house visit from today onwards, per household. Lets the overview
// answer "when are we going there?" without opening the household. Today counts
// in full: see startOfDay.
//
// `now` is a parameter rather than a module-level constant: a date captured at
// import time goes stale in a long-lived tab, and the tests would have to move
// with the calendar.
// AI-CONTEXT: See docs/pitfalls.md#state-stale-module-level-timestamp

import isAppointment from './taskKind'

/**
 * @param {{Timestamp?: {toDate: () => Date}}} task
 * @returns {number|null} Epoch ms, or null when the task carries no usable date
 */
const visitTime = (task) => {
    if (typeof task?.Timestamp?.toDate !== 'function') return null
    const time = task.Timestamp.toDate().getTime()
    return Number.isNaN(time) ? null : time
}

/**
 * Het begin van de dag waarop `now` valt, in de tijdzone van de gebruiker.
 *
 * De grens is de DAG en niet het moment zelf: een planner die om 14:00 in het
 * overzicht kijkt moet het bezoek van vanmorgen 09:00 nog zien staan, want dat
 * huishouden is vandaag aan de beurt. Viel het weg zodra de klok erlangs was,
 * dan leek de kolom stuk in plaats van leeg.
 *
 * Lokale middernacht, niet UTC: 'vandaag' is de dag van de planner zelf.
 *
 * @param {Date} now - Referentiemoment
 * @returns {number} Epoch ms van middernacht
 */
const startOfDay = (now) => {
    const begin = new Date(now.getTime())
    begin.setHours(0, 0, 0, 0)
    return begin.getTime()
}

/**
 * @param {Array<object>} [tasks] - Every Tasks doc of the tenant
 * @param {Date} [now] - Reference moment; anything before that day counts as past
 * @returns {Record<string, object>} The next visit per AdressID; households without one are absent
 */
const nextVisitPerAdress = (tasks, now = new Date()) => {
    const vanafMs = startOfDay(now)

    /** @type {Record<string, object>} */
    const result = {}

    ;(Array.isArray(tasks) ? tasks : []).forEach((task) => {
        // isAppointment, niet Type: een subtaak droeg hetzelfde Type als zijn
        // afspraak, en de nieuwe vorm heeft niet eens een Parent. Allebei zijn
        // werk, geen afspraak, en horen niet in deze kolom.
        // AI-CONTEXT: See docs/features/subtaken-bij-het-huishouden.md
        if (!isAppointment(task)) return
        if (!task.AdressID) return
        // Een concept ontstaat zodra iemand een tijdslot aanklikt en wordt pas
        // bij opslaan bevestigd; een afgebroken venster laat er een achter.
        // Alleen 'draft' uitsluiten en niet 'alles behalve confirmed': oudere
        // afspraken dragen helemaal geen Status en zouden dan stil wegvallen.
        if (task.Status === 'draft') return

        const time = visitTime(task)
        if (time === null || time < vanafMs) return

        const current = result[task.AdressID]
        if (!current || time < visitTime(current)) result[task.AdressID] = task
    })

    return result
}

export default nextVisitPerAdress
